<script setup lang="ts">
import { Link } from '@inertiajs/vue3';
import { ArrowLeft } from 'lucide-vue-next';
import RichContent from '@/components/RichContent.vue';
import PublicLayout from '@/layouts/PublicLayout.vue';
import type { Post } from '@/types';

type Props = {
    post: Post;
    /** Its form in the administration, for a signed-in account only. */
    editUrl: string | null;
};

defineProps<Props>();

function formatDate(date: string | null): string {
    if (!date) return '';
    return new Date(date).toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
    });
}
</script>

<template>
    <PublicLayout :edit-url="editUrl" edit-label="Modifier cette actualité">
        <article class="mx-auto max-w-3xl px-4 py-16 sm:px-6 lg:px-8">
            <!-- Back link -->
            <Link
                href="/actualites"
                class="mb-8 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
            >
                <ArrowLeft class="h-4 w-4" />
                Retour aux actualités
            </Link>

            <!-- Header -->
            <header class="mb-8">
                <time
                    v-if="post.published_at"
                    class="text-sm text-muted-foreground"
                    :datetime="post.published_at"
                >
                    {{ formatDate(post.published_at) }}
                </time>
                <h1 class="mt-2 text-3xl font-bold tracking-tight sm:text-4xl">
                    {{ post.title }}
                </h1>
                <p
                    v-if="post.excerpt"
                    class="mt-4 text-lg text-muted-foreground"
                >
                    {{ post.excerpt }}
                </p>
            </header>

            <!-- Cover image -->
            <img
                v-if="post.image"
                :src="`/storage/${post.image}`"
                alt=""
                class="mb-8 w-full rounded-xl object-cover"
                loading="eager"
                width="768"
                height="432"
            />

            <!-- Content -->
            <RichContent :html="post.content" />
        </article>
    </PublicLayout>
</template>
